import { saveAs } from 'file-saver';
import { get, post, remove } from './request';
import { postFileAxios } from './file';
import * as Storage from './storage';

export type YelumImportStatus =
  | 'DRAFT'
  | 'PENDING'
  | 'RUNNING'
  | 'SUCCESS'
  | 'COMPLETED'
  | 'COMPLETED_WITH_ERRORS'
  | 'FAILED'
  | 'CANCELLED';

export interface YelumImportItem {
  id?: string;
  itemNo?: string | number;
  policyNumber?: string | number;
  insuredName?: string;
  insuredDocument?: string;
  address?: string;
  status?: string;
  matchStatus?: string;
  matchType?: string;
  message?: string;
  errorMessage?: string;
  localPolicyId?: string;
  localPolicy?: Record<string, any>;
  spreadsheetData?: Record<string, any>;
  matchedPolicy?: Record<string, any>;
  [key: string]: any;
}

export interface YelumBatchImport {
  id: string;
  estateId?: string;
  estate?: { id?: string; name?: string };
  fileName?: string;
  filename?: string;
  originalFileName?: string;
  status: YelumImportStatus | string;
  progress?: number;
  processedItems?: number;
  totalItems?: number;
  matchedItems?: number;
  successItems?: number;
  failedItems?: number;
  canceledItems?: number;
  skippedItems?: number;
  cancelMatchedPolicies?: boolean;
  processInsurerCancellation?: boolean;
  generateInvoices?: boolean;
  sendReportEmail?: boolean;
  cancelMissingItems?: boolean;
  processMatchedItems?: boolean;
  cancelItemsNotInFile?: boolean;
  processItems?: boolean;
  items?: YelumImportItem[];
  warnings?: string[];
  results?: YelumImportItem[];
  errorMessage?: string;
  createdAt?: string;
  updatedAt?: string;
  completedAt?: string;
  [key: string]: any;
}

const normalizeListResponse = (response: any): YelumBatchImport[] => {
  if (Array.isArray(response)) return response;
  return response?.items || response?.data || response?.imports || [];
};

export const listYelumBatchImports = async (
  estateId?: string,
  status?: string
): Promise<YelumBatchImport[]> => {
  const params = new URLSearchParams();
  if (estateId) params.set('estateId', estateId);
  if (status) params.set('status', status);
  const query = params.toString();
  return normalizeListResponse(await get(`/yelum-batch-imports${query ? `?${query}` : ''}`));
};

export const getYelumBatchImport = async (id: string): Promise<YelumBatchImport> => {
  return get(`/yelum-batch-imports/${id}`);
};

export const uploadYelumBatchImport = async (
  file: File,
  estateId: string
): Promise<YelumBatchImport> => {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('estateId', estateId);

  const response = await postFileAxios(formData, {
    tableName: 'yelum_batch_imports',
    registerId: estateId,
    name: file.name,
    endpoint: '/yelum-batch-imports/upload'
  });
  return response as unknown as YelumBatchImport;
};

export const confirmYelumBatchImport = async (
  id: string,
  payload: {
    cancelMatchedPolicies: boolean;
    processInsurerCancellation: boolean;
  }
): Promise<YelumBatchImport> => {
  return post(`/yelum-batch-imports/${id}/confirm`, payload);
};

export const deleteYelumBatchImport = async (id: string): Promise<void> => {
  await remove(`/yelum-batch-imports/${id}`);
};

export const downloadYelumBatchImportReport = async (id: string): Promise<void> => {
  const response = await fetch(
    `${process.env.REACT_APP_API_URL}/yelum-batch-imports/${id}/report`,
    {
      headers: {
        Authorization: Storage.get('id_token') || '',
        'X-External-Login': Storage.get('external_login') || ''
      }
    }
  );

  if (!response.ok) {
    let message = 'Não foi possível baixar o relatório.';
    try {
      const body = await response.json();
      message = body?.message || body?.error || message;
    } catch {
      // A resposta pode ser um erro sem corpo JSON.
    }
    throw new Error(message);
  }

  let reportResponse = response;
  const contentType = response.headers.get('Content-Type') || '';
  if (contentType.includes('application/json')) {
    const body = await response.json();
    const reportUrl = body?.url || body?.downloadUrl;
    if (!reportUrl) {
      throw new Error(body?.message || 'O relatório ainda não está disponível.');
    }
    reportResponse = await fetch(reportUrl);
    if (!reportResponse.ok) {
      throw new Error('Não foi possível baixar o arquivo do relatório.');
    }
  }

  const blob = await reportResponse.blob();
  const disposition = reportResponse.headers.get('Content-Disposition') || '';
  const filename =
    disposition.match(/filename="?([^"]+)"?/)?.[1] ||
    `relatorio-importacao-yelum-${id}.xlsx`;

  saveAs(blob, filename);
};
